import type { EstateCategory } from "@/shared/types/estateTypes";

import {
  Home,
  Shield,
  Car,
  TrendingUp,
  Wallet,
  Gem,
  CreditCard,
  ReceiptText,
  Landmark,
  Briefcase,
  Coins,
  PiggyBank,
  Bitcoin,
  Image,
  Globe,
  HeartPulse,
  Calendar,
  Vault,
  BarChart2,
  Store,
  Award,
  Copyright,
  Tag,
  Building2,
  AlertTriangle,
  FileText,
  Umbrella,
  Package,
  Ticket,
} from "lucide-react";

export const ASSET_CATEGORIES = [
  "Properties",
  "Bank accounts",
  "Brokerage accounts",
  "Tax-advantaged savings",
  "Retirement accounts",
  "Crypto accounts",
  "NFTs",
  "Other digital assets",
  "Life insurance",
  "Long-term care insurance",
  "Annuity contracts",
  "Vehicles",
  "Safe or safety deposit box",
  "Valuables",
  "LLC or partnership interest",
  "Stock in a private company",
  "Sole proprietorship",
  "Receivables",
  "Work-related pay and shares",
  "Royalties and Intellectual Property",
  "Prepaid Services and Credits",
] as const;

export const DEBT_CATEGORIES = [
  "Unsecured Debts",
  "Secured debts",
  "Ongoing Estate Expenses",
  "Contingent Liabilities",
  "Tax Liabilities",
] as const;

export interface InventoryCategoryGroup {
  title: string;
  subCategories: readonly string[];
  classification: "asset" | "debt";
}

export const INVENTORY_CATEGORY_GROUPS: readonly InventoryCategoryGroup[] = [
  {
    title: "Real Estate",
    classification: "asset",
    subCategories: ["Properties"],
  },
  {
    title: "Financial Accounts",
    classification: "asset",
    subCategories: [
      "Bank accounts",
      "Brokerage accounts",
      "Tax-advantaged savings",
      "Retirement accounts",
      "Crypto accounts",
      "NFTs",
      "Other digital assets",
    ],
  },
  {
    title: "Insurance",
    classification: "asset",
    subCategories: ["Life insurance", "Long-term care insurance", "Annuity contracts"],
  },
  {
    title: "Tangible Personal Property",
    classification: "asset",
    subCategories: ["Vehicles", "Valuables", "Safe or safety deposit box"],
  },
  {
    title: "Business Interests",
    classification: "asset",
    subCategories: [
      "LLC or partnership interest",
      "Stock in a private company",
      "Sole proprietorship",
    ],
  },
  {
    title: "Other Assets",
    classification: "asset",
    subCategories: [
      "Receivables",
      "Work-related pay and shares",
      "Royalties and Intellectual Property",
      "Prepaid Services and Credits",
    ],
  },
  {
    title: "Debts and Liabilities",
    classification: "debt",
    subCategories: ["Unsecured Debts", "Secured debts"],
  },
  {
    title: "Ongoing Estate Expenses",
    classification: "debt",
    subCategories: ["Ongoing Estate Expenses"],
  },
  {
    title: "Other Debts",
    classification: "debt",
    subCategories: ["Contingent Liabilities", "Tax Liabilities"],
  },
];

export const ACCOUNT_TYPES: Record<string, string[]> = {
  // Real Estate
  Properties: ["Properties", "Rental properties"],

  // Financial Accounts
  "Bank accounts": ["Checking accounts", "Savings accounts", "CDs or money market accounts"],
  "Brokerage accounts": ["Brokerage accounts"],
  "Tax-advantaged savings": [
    "529 plans",
    "Health Savings Accounts (HSA)",
    "Flexible Savings Accounts (FSA)",
  ],
  "Retirement accounts": [
    "IRA (Traditional / Roth / SEP / SIMPLE)",
    "401(k)s or other employer plan",
    "Pensions with survivor benefit",
  ],
  "Crypto accounts": ["Crypto accounts"],
  NFTs: ["NFTs"],
  "Other digital assets": ["Other digital assets"],

  // Insurance
  "Life insurance": [
    "Term life insurance",
    "Whole or universal life insurance",
    "Group life insurance",
  ],
  "Long-term care insurance": ["Long-term care insurance"],
  "Annuity contracts": ["Annuity contracts"],

  // Tangible Personal Property
  Vehicles: ["Car", "Motorcycle", "Boat", "RV", "Other"],
  "Safe or safety deposit box": ["Bank Safe Deposit Box", "Home Safe", "Storage Facility", "Other"],
  Valuables: [
    "Jewelry",
    "Art & Paintings",
    "Collections (coins, stamps, etc.)",
    "Antiques & Furniture",
    "Memorabilia",
    "Musical Instruments",
    "Other valuables",
  ],

  // Business Interests
  "LLC or partnership interest": ["LLC or partnership interest"],
  "Stock in a private company": ["Stock in a private company"],
  "Sole proprietorship": ["Sole proprietorship"],

  // Other Assets
  Receivables: [
    "Loans made to someone else",
    "Final paychecks or unpaid wages",
    "Refunds in process",
    "Legal settlements",
    "Long-term care benefits owed",
  ],
  "Work-related pay and shares": [
    "Vested stock options (ISOs / NSOs)",
    "Restricted stock units",
    "Deferred compensation plans",
    "Executive retirement plans",
  ],
  "Royalties and Intellectual Property": [
    "Patents",
    "Books or music royalties",
    "Trademark or licensing deal",
  ],
  "Prepaid Services and Credits": [
    "Prepaid funeral plan",
    "Gift cards and store credits",
    "Airline miles and loyalty points",
  ],

  // Debts and Liabilities
  "Unsecured Debts": [
    "Credit card debt",
    "Medical bills",
    "Personal loans",
    "Unpaid alimony or child support",
  ],
  "Secured debts": ["Mortgages", "Home equity loans", "Auto loans"],

  // Ongoing Estate Expenses
  "Ongoing Estate Expenses": [
    "Funeral and burial costs",
    "Ongoing property costs",
    "Storage units",
    "Subscriptions",
    "Professional and service fees",
  ],

  // Other Debts
  "Contingent Liabilities": [
    "Loan or lease cosigned for someone else",
    "Rent owed",
    "Pending lawsuits against decedent",
  ],
  "Tax Liabilities": [
    "Unfiled tax returns",
    "Tax payment plans",
    "State estate or inheritance taxes",
  ],
};

export const CATEGORY_ICONS = {
  // Real Estate
  Properties: Home,

  // Financial Accounts
  "Bank accounts": Wallet,
  "Brokerage accounts": TrendingUp,
  "Tax-advantaged savings": PiggyBank,
  "Retirement accounts": Landmark,
  "Crypto accounts": Bitcoin,
  NFTs: Image,
  "Other digital assets": Globe,

  // Insurance
  "Life insurance": Shield,
  "Long-term care insurance": HeartPulse,
  "Annuity contracts": Calendar,

  // Tangible Personal Property
  Vehicles: Car,
  "Safe or safety deposit box": Vault,
  Valuables: Gem,

  // Business Interests
  "LLC or partnership interest": Briefcase,
  "Stock in a private company": BarChart2,
  "Sole proprietorship": Store,

  // Other Assets
  Receivables: Coins,
  "Work-related pay and shares": Award,
  "Royalties and Intellectual Property": Copyright,
  "Prepaid Services and Credits": Tag,

  // Debts and Liabilities
  "Unsecured Debts": CreditCard,
  "Secured debts": Building2,

  // Ongoing Estate Expenses
  "Ongoing Estate Expenses": ReceiptText,

  // Other Debts
  "Contingent Liabilities": AlertTriangle,
  "Tax Liabilities": FileText,
};

export const ITEM_TYPE_ICONS: Record<string, typeof Car> = {
  "Auto loans": Car,
};

export function getItemIcon(category: string, itemType?: string | null) {
  if (itemType && ITEM_TYPE_ICONS[itemType]) return ITEM_TYPE_ICONS[itemType];
  return CATEGORY_ICONS[category as keyof typeof CATEGORY_ICONS];
}

export const GROUP_ICONS = {
  "Real Estate": Home,
  "Financial Accounts": Wallet,
  Insurance: Umbrella,
  "Tangible Personal Property": Package,
  "Business Interests": Building2,
  "Other Assets": PiggyBank,
  "Debts and Liabilities": Ticket,
  "Ongoing Estate Expenses": ReceiptText,
  "Other Debts": Landmark,
};

export function filterByCategory<T extends { category: string }>(
  items: T[],
  category: EstateCategory,
): T[] {
  return items.filter((item) => item.category === category);
}
